import React, {useContext, useEffect, useMemo, useState} from "react";
import {AiReportSummarizerServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table, Tag} from "antd";
import {CopyOutlined, DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useLocation, useNavigate} from "react-router-dom";
import MarkdownContent from "../common/MarkdownContent";
import _ from "lodash";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiReportSummarizerList.module.css";
import AiReportSummarizer from "../../domain/AiReportSummarizer";
import {AiExtractorType, AiExtractorWebSearchMode, formatAiExtractorType, formatAiExtractorWebSearchMode, formatExternalWebSearchBackend} from "../../domain/AiExtractor";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {Permission} from "../../domain/auth/Permission";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import Tip from "../common/Tip";
import {FormPersister} from "../../sal-ui/FormPersister";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

const PERSISTENT_IDENT = "AiReportSummarizerList";
const FILTER_DEBOUNCE_MS = 500;

function AiReportSummarizerList() {
    const appContext = useContext(AppContextContext);
    const aiReportSummarizerService = useContext(AiReportSummarizerServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [summarizers, setSummarizers] = useState<PagedResult<AiReportSummarizer>>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);
    const title = "AI report summarizers";
    const isSystemAdmin = appContext.user.isSystemAdmin();
    const hasManage = isSystemAdmin || appContext.user.hasPermission(Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG);
    const hasOrganizationAccess = appContext.user.hasPermission(Permission.AI_REPORT_SUMMARIZER__READ_ORG)
                                  || appContext.user.hasPermission(Permission.AI_REPORT_SUMMARIZER__MANAGE_ORG);
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.AI_REPORT_SUMMARIZER__READ_SEAL) || hasOrganizationAccess,
        canReadOrganization: hasOrganizationAccess
    };

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <Tip
                    id="AiReportSummarizerList.purpose"
                    style={{marginBottom: 12}}
                    message="AI report summarizers define how SEAL uses large language models (LLM) to summarize vulnerability reports."
                    description="SEAL uses them to create the AI summary section on vulnerability report details."
                />

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/definitions/ai-report-summarizers/add")}>
                            Add
                        </Button>
                    }
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table
                    className={styles.table}
                    dataSource={summarizers?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    onChange={tableHandler.onTableChange}
                    columns={getColumns()}
                />
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<AiReportSummarizer> {
        const cols: ColumnsType<AiReportSummarizer> = [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: (value, summarizer: AiReportSummarizer) =>
                    <div className={styles["name-cell"]}>
                        <div>
                            <Link to={`/definitions/ai-report-summarizers/${summarizer.id}`}>{value}</Link>
                            {summarizer.defaultSummarizer &&
                                <Tag color="blue" style={{marginLeft: 8}}>default</Tag>}
                        </div>

                        {summarizer.description &&
                            <SingleSummaryBlock>
                                <MarkdownContent children={summarizer.description}/>
                            </SingleSummaryBlock>}

                        {(summarizer.systemPrompt?.name || summarizer.supplementaryPrompt?.name) && (
                            <div className={styles["ai-config-tags"]}>
                                {summarizer.systemPrompt?.name &&
                                    <Tag color="blue" bordered={true}>{summarizer.systemPrompt.name}</Tag>}
                                {summarizer.supplementaryPrompt?.name &&
                                    <Tag color="cyan" bordered={true}>+ {summarizer.supplementaryPrompt.name}</Tag>}
                            </div>
                        )}
                    </div>
            },
            {
                dataIndex: ["organization", "name"],
                key: "organization.name",
                title: "Owner",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                align: "left",
                render: renderOrganization
            },
            {dataIndex: "type", title: "Type", width: 160, render: value => formatAiExtractorType(value)},
            {
                dataIndex: "model",
                title: "Model",
                render: (value, summarizer: AiReportSummarizer) => (
                    <div className={styles["model-cell"]}>
                        <div>{value || <i>no model</i>}</div>
                        {renderModelTags(summarizer, isSystemAdmin)}
                    </div>
                )
            },
        ];

        if (hasManage) {
            cols.push({
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            });
        }

        return cols;
    }

    function renderOrganization(value: any, summarizer: AiReportSummarizer) {
        if (summarizer.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(summarizer.organization, value);
    }

    function renderAction(_: unknown, summarizer: AiReportSummarizer) {
        return (
            <Space>
                <Button icon={<CopyOutlined/>} title="Clone" onClick={() => navigate(`/definitions/ai-report-summarizers/clone/${summarizer.id}`)}/>
                {isEditableForPrincipal(summarizer) && <Button
                    icon={<DeleteFilled/>}
                    className={"ant-btn-icon-only"}
                    title={summarizer.inUse ? "This summarizer is in use and cannot be deleted" : "Delete"}
                    disabled={summarizer.inUse}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete AI report summarizer <b>{summarizer.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            okButtonProps: {danger: true},
                            onOk: () => onDeleteConfirm(summarizer)
                        });
                    }}
                />}
            </Space>
        );
    }

    function isEditableForPrincipal(summarizer: AiReportSummarizer) {
        return summarizer?.owner !== ResourceOwnerType.SYSTEM || isSystemAdmin;
    }

    function onDeleteConfirm(summarizer: AiReportSummarizer) {
        aiReportSummarizerService.delete(summarizer)
            .then(() => {
                message.success("AI report summarizer deleted.");
                tableHandler.reload();
            });
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return aiReportSummarizerService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);

                setSummarizers(result);
            });
    }
}

function buildModelTagLabels(summarizer: AiReportSummarizer, isSystemAdmin: boolean): string[] {
    const labels: string[] = [];
    const webSearchMode = effectiveWebSearchMode(summarizer);

    if (summarizer.type === AiExtractorType.OPENAI_API && isSystemAdmin && summarizer.openAiServiceTier) {
        labels.push(`service tier: ${String(summarizer.openAiServiceTier).toLowerCase()}`);
    }

    if ((summarizer.type === AiExtractorType.OPENAI_API || summarizer.type === AiExtractorType.OPENAI_COMPATIBLE_API)
        && isSystemAdmin
        && summarizer.openAiReasoningEffort) {
        labels.push(`reasoning: ${String(summarizer.openAiReasoningEffort).toLowerCase()}`);
    }

    if (summarizer.type === AiExtractorType.OPENAI_COMPATIBLE_API && summarizer.aiProviderConnection?.name) {
        labels.push(`provider: ${summarizer.aiProviderConnection.name}`);
    }

    if (summarizer.type === AiExtractorType.GROK_API && isSystemAdmin && summarizer.grokReasoningEffort) {
        labels.push(`reasoning: ${String(summarizer.grokReasoningEffort).toLowerCase()}`);
    }

    if (summarizer.type === AiExtractorType.ANTHROPIC_API && isSystemAdmin && summarizer.anthropicReasoningEffort) {
        labels.push(`reasoning: ${String(summarizer.anthropicReasoningEffort).toLowerCase()}`);
    }

    if (webSearchMode !== AiExtractorWebSearchMode.DISABLED) {
        labels.push(`web: ${formatAiExtractorWebSearchMode(webSearchMode)}`);
    }

    if (webSearchMode === AiExtractorWebSearchMode.EXTERNAL && summarizer.externalWebSearchBackend) {
        labels.push(`backend: ${formatExternalWebSearchBackend(summarizer.externalWebSearchBackend)}`);
    }

    return labels;
}

function renderModelTags(summarizer: AiReportSummarizer, isSystemAdmin: boolean) {
    const labels = buildModelTagLabels(summarizer, isSystemAdmin);

    if (labels.length === 0) {
        return null;
    }

    return (
        <div className={styles["model-tags"]}>
            {labels.map(label => <Tag key={label}>{label}</Tag>)}
        </div>
    );
}

function effectiveWebSearchMode(summarizer: AiReportSummarizer) {
    return summarizer.type === AiExtractorType.OLLAMA
        ? AiExtractorWebSearchMode.DISABLED
        : summarizer.webSearchMode || AiExtractorWebSearchMode.DISABLED;
}

export default AiReportSummarizerList;
